import { FormEvent, useEffect, useMemo, useState } from "react";
import { Edit, ExternalLink, FileText, Plus, RefreshCw, Save, ShieldAlert, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminMenuItem,
  AdminMenuItemPayload,
  AdminMenuItemType,
  AdminPermissionCatalogItem,
  AdminUser,
  createMenuItem,
  deleteMenuItem,
  getCurrentAdmin,
  listMenuItems,
  logout,
  updateMenuItem,
} from "./api";
import { useConfirmDialog } from "./components/ConfirmDialog";
import { AdminRadioCardGroup, AdminSwitchCard } from "./components/AdminChoiceControl";
import { ADMIN_MENU_ICON_KEYS, resolveAdminMenuIcon } from "./adminMenuIcons";

type FormMode = "group" | "child";

type FormState = {
  ItemType: AdminMenuItemType;
  Label: string;
  Icon: string;
  ParentId: string;
  PermissionKey: string;
  Path: string;
  SortOrder: string;
  IsActive: boolean;
};

function emptyGroupForm(): FormState {
  return {
    ItemType: "Group",
    Label: "",
    Icon: ADMIN_MENU_ICON_KEYS[0] || "",
    ParentId: "",
    PermissionKey: "",
    Path: "",
    SortOrder: "100",
    IsActive: true,
  };
}

function emptyChildForm(parentId: string): FormState {
  return {
    ItemType: "Page",
    Label: "",
    Icon: "",
    ParentId: parentId,
    PermissionKey: "",
    Path: "",
    SortOrder: "100",
    IsActive: true,
  };
}

function itemToForm(item: AdminMenuItem): FormState {
  return {
    ItemType: item.ItemType,
    Label: item.Label,
    Icon: item.Icon || ADMIN_MENU_ICON_KEYS[0] || "",
    ParentId: item.ParentId || "",
    PermissionKey: item.PermissionKey || "",
    Path: item.Path || "",
    SortOrder: String(item.SortOrder ?? 100),
    IsActive: item.IsActive,
  };
}

function sortItems(items: AdminMenuItem[]): AdminMenuItem[] {
  return [...items].sort((a, b) => a.SortOrder - b.SortOrder || a.AdminMenuItemId.localeCompare(b.AdminMenuItemId));
}

const inputClass = "h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";
const selectClass = "h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";
const labelClass = "grid gap-2 text-sm font-bold text-[#315746]";

function TypeBadge({ type }: { type: AdminMenuItemType }) {
  if (type === "External") {
    return <span className="inline-flex items-center gap-1 rounded-full bg-[#f3ecdf] px-2.5 py-1 text-xs font-bold text-[#8a6a2f]"><ExternalLink size={12} />外部連結</span>;
  }
  return <span className="inline-flex items-center gap-1 rounded-full bg-[#eef3f8] px-2.5 py-1 text-xs font-bold text-[#255071]"><FileText size={12} />頁面連結</span>;
}

function ActiveBadge({ isActive }: { isActive: boolean }) {
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${isActive ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>
      {isActive ? "啟用" : "停用"}
    </span>
  );
}

export default function AdminMenuItems() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState("");
  const [items, setItems] = useState<AdminMenuItem[]>([]);
  const [permissionCatalog, setPermissionCatalog] = useState<AdminPermissionCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState<{ tone: "error" | "success"; text: string } | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AdminMenuItem | null>(null);
  const [form, setForm] = useState<FormState>(emptyGroupForm());
  const [formMessage, setFormMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load() {
    setLoading(true);
    setBanner(null);
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
    } catch (error) {
      setBanner({ tone: "error", text: error instanceof Error ? error.message : "選單資料載入失敗。" });
      navigate("/admin-login", { replace: true });
      setLoading(false);
      return;
    }

    // 「選單維護」現在是一般的頁面權限（admin-menu），不再只限超級管理者，實際看不看得到這頁
    // 由後端 /api/admin/menu-items 的權限檢查決定，前端直接呼叫、依成功或 403 來判斷即可，
    // 不用自己重複判斷 IsSuperAdmin（超級管理者一律略過檢查，後端會自動放行）。
    try {
      const data = await listMenuItems();
      setAccessDenied(false);
      setItems(data.Items);
      setPermissionCatalog(data.PermissionCatalog);
    } catch (error) {
      setAccessDenied(true);
      setAccessDeniedMessage(error instanceof Error ? error.message : "此帳號沒有維護選單結構的權限。");
      setItems([]);
      setPermissionCatalog([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  const groups = useMemo(() => sortItems(items.filter((item) => item.ItemType === "Group" && item.ParentId === null)), [items]);
  const bareTopLevel = useMemo(() => sortItems(items.filter((item) => item.ItemType !== "Group" && item.ParentId === null)), [items]);
  const permissionLabelByKey = useMemo(() => {
    const map: Record<string, string> = {};
    permissionCatalog.forEach((entry) => { map[entry.Key] = entry.Label; });
    return map;
  }, [permissionCatalog]);

  function childrenOf(groupId: string): AdminMenuItem[] {
    return sortItems(items.filter((item) => item.ParentId === groupId));
  }

  function openCreateGroup() {
    setEditingItem(null);
    setForm(emptyGroupForm());
    setFormMessage("");
    setFormOpen(true);
  }

  function openCreateChild(parentId: string | null) {
    setEditingItem(null);
    setForm(emptyChildForm(parentId || ""));
    setFormMessage("");
    setFormOpen(true);
  }

  function openEditItem(item: AdminMenuItem) {
    setEditingItem(item);
    setForm(itemToForm(item));
    setFormMessage("");
    setFormOpen(true);
  }

  function closeForm() {
    if (saving) return;
    setFormOpen(false);
    setEditingItem(null);
    setFormMessage("");
  }

  const formMode: FormMode = form.ItemType === "Group" ? "group" : "child";

  function handleChildTypeChange(nextType: "Page" | "External") {
    setForm((current) => ({
      ...current,
      ItemType: nextType,
      Path: "",
      PermissionKey: nextType === "Page" ? current.PermissionKey : "",
    }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const label = form.Label.trim();
    if (!label) { setFormMessage("請輸入選單名稱。"); return; }
    if (label.length > 60) { setFormMessage("選單名稱請勿超過 60 個字。"); return; }

    let payload: AdminMenuItemPayload;

    if (formMode === "group") {
      payload = {
        ItemType: "Group",
        Label: label,
        Icon: form.Icon || undefined,
        ParentId: null,
        SortOrder: Number(form.SortOrder || 100),
        IsActive: form.IsActive,
      };
    } else {
      const path = form.Path.trim();
      if (form.ItemType === "Page") {
        if (!form.PermissionKey) { setFormMessage("請選擇頁面權限。"); return; }
      } else {
        if (!/^https?:\/\//.test(path)) { setFormMessage("外部連結請填寫完整網址，需以 http:// 或 https:// 開頭。"); return; }
      }

      payload = {
        ItemType: form.ItemType,
        Label: label,
        ParentId: form.ParentId || null,
        SortOrder: Number(form.SortOrder || 100),
        IsActive: form.IsActive,
      };
      if (form.ItemType === "Page") {
        // Page 類型的路徑一律由後端依 PermissionKey 從 AdminPermissionCatalog 自動帶出、強制覆寫，
        // 不接受前端自訂路徑，避免出現選單項目對不到真實頁面的死連結，這裡不用送 Path。
        payload.PermissionKey = form.PermissionKey;
      } else {
        payload.Path = path;
      }
    }

    setSaving(true);
    setFormMessage("");
    try {
      const successText = editingItem ? "選單項目已更新。" : "選單項目已新增。";
      if (editingItem) {
        await updateMenuItem(editingItem.AdminMenuItemId, payload);
      } else {
        await createMenuItem(payload);
      }
      closeForm();
      await load();
      setBanner({ tone: "success", text: successText });
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "選單項目儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteGroup(group: AdminMenuItem) {
    const children = childrenOf(group.AdminMenuItemId);
    if (children.length > 0) {
      setBanner({ tone: "error", text: `「${group.Label}」分組底下還有 ${children.length} 個項目，請先移除或搬移後才能刪除。` });
      return;
    }

    const confirmed = await confirm({
      title: "刪除這個分組？",
      description: (
        <>
          確定要刪除分組 <strong className="font-bold text-[#203d32]">{group.Label}</strong> 嗎？
          <br />
          刪除後側邊選單將不再顯示此分組。
        </>
      ),
      confirmText: "刪除分組",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteMenuItem(group.AdminMenuItemId);
      await load();
      setBanner({ tone: "success", text: "分組已刪除。" });
    } catch (error) {
      setBanner({ tone: "error", text: error instanceof Error ? error.message : "分組刪除失敗。" });
    }
  }

  async function handleDeleteChild(item: AdminMenuItem) {
    const confirmed = await confirm({
      title: "刪除這個選單項目？",
      description: (
        <>
          確定要刪除 <strong className="font-bold text-[#203d32]">{item.Label}</strong> 嗎？
          <br />
          刪除後側邊選單將不再顯示此項目。
        </>
      ),
      confirmText: "刪除項目",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteMenuItem(item.AdminMenuItemId);
      await load();
      setBanner({ tone: "success", text: "選單項目已刪除。" });
    } catch (error) {
      setBanner({ tone: "error", text: error instanceof Error ? error.message : "選單項目刪除失敗。" });
    }
  }

  function ChildRow({ item }: { item: AdminMenuItem }) {
    return (
      <tr className="border-t border-[#edf0ee]">
        <td className="px-4 py-3">
          <p className="font-bold text-[#203d32]">{item.Label}</p>
        </td>
        <td className="px-4 py-3"><TypeBadge type={item.ItemType} /></td>
        <td className="px-4 py-3 text-sm text-[#59665f]">{item.ItemType === "Page" ? (item.PermissionLabel || permissionLabelByKey[item.PermissionKey || ""] || "-") : "不限（任何管理員可見）"}</td>
        <td className="max-w-[280px] break-all px-4 py-3 text-sm text-[#59665f]">{item.Path || "-"}</td>
        <td className="px-4 py-3"><ActiveBadge isActive={item.IsActive} /></td>
        <td className="px-4 py-3 text-sm text-[#59665f]">{item.SortOrder}</td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-2">
            <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => openEditItem(item)} type="button">
              <Edit size={15} />編輯
            </button>
            {/* <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#f0c7c4] bg-white px-3 text-sm font-bold text-[#9a2d2d] hover:bg-[#fdebea]" onClick={() => handleDeleteChild(item)} type="button">
              <Trash2 size={15} />刪除
            </button> */}
          </div>
        </td>
      </tr>
    );
  }

  function ChildTable({ children, emptyText }: { children: AdminMenuItem[]; emptyText: string }) {
    if (children.length === 0) {
      return <div className="px-4 py-6 text-center text-sm text-[#9aa59f]">{emptyText}</div>;
    }
    return (
      <div className="overflow-auto">
        <table className="w-full min-w-[860px] border-collapse text-left">
          <thead className="bg-[#f6f7f5] text-xs text-[#7b877f]">
            <tr>
              <th className="px-4 py-2">名稱</th>
              <th className="px-4 py-2">類型</th>
              <th className="px-4 py-2">頁面權限</th>
              <th className="px-4 py-2">路徑 / 網址</th>
              <th className="px-4 py-2">狀態</th>
              <th className="px-4 py-2">排序</th>
              <th className="px-4 py-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {children.map((item) => <ChildRow item={item} key={item.AdminMenuItemId} />)}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <AdminLayout sectionLabel="系統管理" title="選單維護" user={currentUser} onLogout={handleLogout}>
      {loading ? (
        <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-16 text-center text-[#59665f]">載入中...</div>
      ) : accessDenied ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-[#f2d9a0] bg-[#fff6df] px-6 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-[#7a5610]">
            <ShieldAlert size={28} />
          </span>
          <h2 className="text-lg font-bold text-[#5c3f0c]">沒有選單維護權限</h2>
          <p className="max-w-[420px] text-sm text-[#7a5610]">{accessDeniedMessage || "此帳號沒有維護選單結構的權限"}，如需調整側邊選單項目，請聯繫具有「選單維護」權限的管理員或超級管理者協助處理。</p>
        </div>
      ) : (
        <>
          <p className="mb-4 text-xs text-[#7b877f]">
            這裡設定的分組、頁面連結與外部連結，會直接決定所有管理員登入後看到的側邊選單內容。頁面連結需指定頁面權限，只有擁有該權限的帳號才看得到；外部連結不受權限限制，任何登入的管理員都能看到。
          </p>

          <div className="mb-4 flex flex-wrap justify-end gap-2">
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => load()} type="button">
              <RefreshCw size={18} />
              重新整理
            </button>
            {/* <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => openCreateChild(null)} type="button">
              <Plus size={20} />
              新增項目
            </button>
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={openCreateGroup} type="button">
              <Plus size={20} />
              新增分組
            </button> */}
          </div>

          {banner && (
            <div
              className={`mb-4 rounded-md border px-4 py-3 text-sm font-medium ${
                banner.tone === "error" ? "border-[#f2b8b5] bg-[#fdebea] text-[#8a1f1f]" : "border-[#bfe0cb] bg-[#e9f4ec] text-[#245438]"
              }`}
            >
              {banner.text}
            </div>
          )}

          <div className="grid gap-4">
            {groups.length === 0 && bareTopLevel.length === 0 && (
              <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-10 text-center text-[#59665f]">尚未建立任何選單項目。</div>
            )}

            {groups.map((group) => {
              const children = childrenOf(group.AdminMenuItemId);
              return (
                <div className="rounded-md border border-[#dfe6e1] bg-white" key={group.AdminMenuItemId}>
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#edf0ee] px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#e9f4ec] text-[#245438]">{resolveAdminMenuIcon(group.Icon)}</span>
                      <div>
                        <p className="font-bold text-[#203d32]">{group.Label}</p>
                        <p className="text-xs text-[#9a8a7a]">排序 {group.SortOrder}</p>
                      </div>
                      <ActiveBadge isActive={group.IsActive} />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {/* <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => openCreateChild(group.AdminMenuItemId)} type="button">
                        <Plus size={15} />新增項目
                      </button> */}
                      <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => openEditItem(group)} type="button">
                        <Edit size={15} />編輯
                      </button>
                      {/* <button
                        className="inline-flex h-9 items-center gap-1 rounded-md border border-[#f0c7c4] bg-white px-3 text-sm font-bold text-[#9a2d2d] hover:bg-[#fdebea] disabled:cursor-not-allowed disabled:opacity-40"
                        disabled={children.length > 0}
                        onClick={() => handleDeleteGroup(group)}
                        title={children.length > 0 ? "此分組底下還有項目，請先移除或搬移後才能刪除。" : undefined}
                        type="button"
                      >
                        <Trash2 size={15} />刪除
                      </button> */}
                    </div>
                  </div>
                  <ChildTable children={children} emptyText="此分組尚未加入任何項目。" />
                </div>
              );
            })}

            {bareTopLevel.length > 0 && (
              <div className="rounded-md border border-[#dfe6e1] bg-white">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#edf0ee] px-4 py-3">
                  <div>
                    <p className="font-bold text-[#203d32]">未分組項目</p>
                    <p className="text-xs text-[#9a8a7a]">不屬於任何分組，會以獨立連結顯示在側邊選單。</p>
                  </div>
                  <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => openCreateChild(null)} type="button">
                    <Plus size={15} />新增項目
                  </button>
                </div>
                <ChildTable children={bareTopLevel} emptyText="沒有未分組的項目。" />
              </div>
            )}
          </div>
        </>
      )}

      {!accessDenied && formOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-[#10251c]/40 px-0 py-0 sm:items-center sm:px-4 sm:py-6">
          <button aria-label="關閉視窗" className="absolute inset-0 cursor-default" onClick={closeForm} type="button" />
          <form className="relative flex max-h-[90vh] w-full max-w-[640px] flex-col overflow-hidden rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.2)] sm:rounded-md" onSubmit={handleSubmit}>
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#edf0ee] px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-[#203d32]">
                  {editingItem ? (formMode === "group" ? "編輯分組" : "編輯選單項目") : formMode === "group" ? "新增分組" : "新增選單項目"}
                </h2>
                <p className="mt-1 text-sm text-[#7b877f]">會直接套用到所有管理員登入後看到的側邊選單。</p>
              </div>
              <button className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-4 overflow-y-auto px-5 py-5">
              {formMessage && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{formMessage}</div>}

              {editingItem ? (
                <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] px-4 py-3 text-sm">
                  <span className="font-bold text-[#315746]">類型：</span>
                  <span className="text-[#59665f]">{form.ItemType === "Group" ? "分組" : form.ItemType === "Page" ? "頁面連結" : "外部連結"}（建立後無法變更，如需改變類型請刪除後重新新增）</span>
                </div>
              ) : formMode === "child" ? (
                <AdminRadioCardGroup
                  name="menu-item-type"
                  onChange={handleChildTypeChange}
                  options={[
                    { value: "Page", label: "頁面連結", description: "連到後台既有頁面，並依權限鍵值控管可見對象。", icon: <FileText size={16} /> },
                    { value: "External", label: "外部連結", description: "連到外部網址，任何登入的管理員都能看到。", icon: <ExternalLink size={16} /> },
                  ]}
                  value={form.ItemType as "Page" | "External"}
                />
              ) : null}

              <label className={labelClass}>
                <span>名稱 <span className="text-[#c3423f]">*</span></span>
                <input className={inputClass} maxLength={60} value={form.Label} onChange={(event) => setForm((current) => ({ ...current, Label: event.target.value }))} />
              </label>

              {formMode === "group" && (
                <label className={labelClass}>
                  <span>圖示</span>
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-[#cfd8d2] bg-[#f6f7f5] text-[#315746]">{resolveAdminMenuIcon(form.Icon)}</span>
                    <select className={selectClass} value={form.Icon} onChange={(event) => setForm((current) => ({ ...current, Icon: event.target.value }))}>
                      {ADMIN_MENU_ICON_KEYS.map((key) => (
                        <option key={key} value={key}>{key}</option>
                      ))}
                    </select>
                  </div>
                </label>
              )}

              {formMode === "child" && (
                <label className={labelClass}>
                  <span>所屬分組</span>
                  <select className={selectClass} value={form.ParentId} onChange={(event) => setForm((current) => ({ ...current, ParentId: event.target.value }))}>
                    <option value="">（無分組，以獨立連結顯示）</option>
                    {groups.map((group) => (
                      <option key={group.AdminMenuItemId} value={group.AdminMenuItemId}>{group.Label}</option>
                    ))}
                  </select>
                </label>
              )}

              {formMode === "child" && form.ItemType === "Page" && (
                <label className={labelClass}>
                  <span>頁面權限 <span className="text-[#c3423f]">*</span></span>
                  <select className={selectClass} value={form.PermissionKey} onChange={(event) => setForm((current) => ({ ...current, PermissionKey: event.target.value }))}>
                    <option value="">請選擇</option>
                    {permissionCatalog.map((entry) => (
                      <option key={entry.Key} value={entry.Key}>{entry.Label}</option>
                    ))}
                  </select>
                </label>
              )}

              {formMode === "child" && form.ItemType === "Page" && (
                <label className={labelClass}>
                  <span>頁面路徑</span>
                  <div className={`${inputClass} flex items-center bg-[#f6f7f5] text-[#59665f]`}>
                    {form.PermissionKey ? (permissionCatalog.find((entry) => entry.Key === form.PermissionKey)?.Path ?? "-") : "請先選擇頁面權限"}
                  </div>
                  <span className="text-xs font-normal text-[#7b877f]">路徑由選擇的頁面權限自動對應，不能自行輸入，避免出現選單項目對不到真實頁面的死連結。</span>
                </label>
              )}

              {formMode === "child" && form.ItemType === "External" && (
                <label className={labelClass}>
                  <span>外部網址 <span className="text-[#c3423f]">*</span></span>
                  <input
                    className={inputClass}
                    placeholder="https://example.com"
                    value={form.Path}
                    onChange={(event) => setForm((current) => ({ ...current, Path: event.target.value }))}
                  />
                </label>
              )}

              <label className={labelClass}>
                排序（數字越小越前面）
                <input className={inputClass} type="number" value={form.SortOrder} onChange={(event) => setForm((current) => ({ ...current, SortOrder: event.target.value }))} />
              </label>

              <AdminSwitchCard
                checked={form.IsActive}
                description={formMode === "group" ? "停用後此分組與底下項目都不會顯示於側邊選單。" : "停用後不會顯示於側邊選單，但不影響既有頁面本身。"}
                label="啟用"
                onChange={(IsActive) => setForm((current) => ({ ...current, IsActive }))}
              />
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-[#edf0ee] px-5 py-4 sm:flex-row sm:justify-end">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                取消
              </button>
              <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving} type="submit">
                <Save size={18} />
                {saving ? "儲存中" : "儲存"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog />
    </AdminLayout>
  );
}
